import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Paper, Box, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const Whiteboard = ({ onStrokes, width = '100%', height = 400, disabled = false, initialStrokes = [], batchInterval = 2000 }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const currentStrokeRef = useRef([]);
    const batchTimeoutRef = useRef(null);
    const pendingStrokesRef = useRef([]);
    const startTimeRef = useRef(Date.now());

    // Reset start time when component mounts
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

    // Handle resize and setup
    useEffect(() => {
        const handleResize = () => {
            const canvas = canvasRef.current;
            if (canvas && canvas.parentElement) {
                const rect = canvas.parentElement.getBoundingClientRect();

                // Only update if size actually changed, OR if we haven't initialized size yet
                if (canvas.width !== rect.width || canvas.height !== (typeof height === 'number' ? height : rect.height) || canvasSize.width === 0) {
                    // Save current content if we are NOT in read-only mode (drawing mode)
                    // If we are in read-only mode (initialStrokes provided), we will just re-render from strokes
                    let imageData = null;
                    const context = canvas.getContext('2d');
                    if ((!initialStrokes || initialStrokes.length === 0) && canvas.width > 0 && canvas.height > 0) {
                        imageData = context.getImageData(0, 0, canvas.width, canvas.height);
                    }

                    // Resize
                    canvas.width = rect.width;
                    canvas.height = typeof height === 'number' ? height : rect.height;

                    setCanvasSize({ width: canvas.width, height: canvas.height });

                    // Restore content if we saved it
                    if (imageData) {
                        context.putImageData(imageData, 0, 0);
                    }

                    // Reset context properties
                    context.lineCap = 'round';
                    context.lineJoin = 'round';
                    context.lineWidth = 2;
                    context.strokeStyle = '#000000';
                }
            }
        };

        window.addEventListener('resize', handleResize);
        // Initial size
        handleResize();

        // Small delay to ensure layout is stable
        setTimeout(handleResize, 100);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (batchTimeoutRef.current) {
                clearTimeout(batchTimeoutRef.current);
            }
        };
    }, [height, initialStrokes, canvasSize.width]); // Add initialStrokes to dependency to know mode

    // Replay initial strokes whenever they change or canvas resizes
    useEffect(() => {
        if (initialStrokes && initialStrokes.length > 0 && canvasRef.current && canvasSize.width > 0) {
            console.log('🎨 Redrawing whiteboard history', {
                strokes: initialStrokes.length,
                size: canvasSize
            });

            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            // Clear first (resize might have cleared it, but good to be sure)
            context.clearRect(0, 0, canvas.width, canvas.height);

            // Reset context properties after clear
            context.lineCap = 'round';
            context.lineJoin = 'round';
            context.lineWidth = 2;
            context.strokeStyle = '#000000';

            context.beginPath();
            initialStrokes.forEach(stroke => {
                if (stroke.type === 'stroke_start') {
                    context.beginPath();
                    context.moveTo(stroke.x, stroke.y);
                } else if (stroke.type === 'stroke_move') {
                    context.lineTo(stroke.x, stroke.y);
                    context.stroke();
                } else if (stroke.type === 'stroke_end') {
                    // Optional: close path or just finish stroke
                    // context.closePath();
                } else if (stroke.type === 'clear') {
                    context.clearRect(0, 0, canvas.width, canvas.height);
                    context.beginPath();
                }
            });
        }
    }, [initialStrokes, canvasSize]);

    const flushStrokes = useCallback(() => {
        if (pendingStrokesRef.current.length > 0) {
            onStrokes([...pendingStrokesRef.current]);
            pendingStrokesRef.current = [];
        }
        batchTimeoutRef.current = null;
    }, [onStrokes]);

    const queueStrokeEvent = useCallback((event) => {
        pendingStrokesRef.current.push(event);

        // If we don't have a timeout, set one to flush in 500ms
        if (!batchTimeoutRef.current) {
            batchTimeoutRef.current = setTimeout(flushStrokes, batchInterval);
        }
    }, [flushStrokes, batchInterval]);

    const getPoint = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        return {
            x: clientX - rect.left,
            y: clientY - rect.top,
            t: Date.now() - startTimeRef.current
        };
    };

    const startDrawing = (e) => {
        if (disabled) return;
        e.preventDefault(); // Prevent scrolling on touch

        const { x, y, t } = getPoint(e);
        setIsDrawing(true);

        const context = canvasRef.current.getContext('2d');
        context.beginPath();
        context.moveTo(x, y);

        const event = { type: 'stroke_start', x, y, t };
        currentStrokeRef.current = [event];
        queueStrokeEvent(event);
    };

    const draw = (e) => {
        if (!isDrawing || disabled) return;
        e.preventDefault();

        const { x, y, t } = getPoint(e);
        const context = canvasRef.current.getContext('2d');
        context.lineTo(x, y);
        context.stroke();

        const event = { type: 'stroke_move', x, y, t };
        currentStrokeRef.current.push(event);
        queueStrokeEvent(event);
    };

    const stopDrawing = () => {
        if (!isDrawing) return;
        setIsDrawing(false);

        const lastPoint = currentStrokeRef.current[currentStrokeRef.current.length - 1];
        if (lastPoint) {
            const event = {
                type: 'stroke_end',
                x: lastPoint.x,
                y: lastPoint.y,
                t: Date.now() - startTimeRef.current
            };
            queueStrokeEvent(event);
        }

        // Flush immediately on stroke end
        if (batchTimeoutRef.current) {
            clearTimeout(batchTimeoutRef.current);
        }
        flushStrokes();
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width, canvas.height);

        // Send a clear event if needed, or just let the AI know via a special event
        // For now, we'll just clear visually. 
        // Ideally we'd send a "clear" event to the backend too.
        const event = { type: 'clear', t: Date.now() - startTimeRef.current };
        onStrokes([event]);
    };

    return (
        <Paper elevation={3} sx={{ p: 1, position: 'relative', overflow: 'hidden' }}>
            {!disabled && (
                <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, display: 'flex', gap: 1 }}>
                    <Tooltip title="Clear Whiteboard">
                        <IconButton onClick={clearCanvas} size="small" sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
            <canvas
                ref={canvasRef}
                style={{
                    touchAction: 'none',
                    cursor: disabled ? 'not-allowed' : 'crosshair',
                    width: '100%',
                    display: 'block'
                }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
            />
        </Paper>
    );
};

export default Whiteboard;
